import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Menu,
  MenuItem,
  Avatar,
  alpha,
} from "@mui/material";
import {
  Autorenew as RenewalsIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  KeyboardArrowDown as ArrowDownIcon,
  OpenInNew as DetailsIcon,
  WarningAmber as GraceIcon,
  Schedule as UpcomingIcon,
  ReceiptLong as RateIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import MdlAddNewBill from "../components/dialog/MdlAddNewBill";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const shellSx = {
  borderRadius: 2.5,
  border: `1px solid ${crmColors.border.default}`,
  boxShadow: "0 6px 18px -14px rgba(15, 31, 47, 0.22)",
  overflow: "hidden",
  background: crmColors.background.paper,
};

//Created By Cursor — e.g. 1 Oct 2025
const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (value) => {
  if (value == null || value === "") return "—";
  return `₹${Number(value).toLocaleString()}`;
};

//Created By Cursor — Grace / Expired chip labels (parity with All Schools)
const getRenewalStatusLabel = (row) => {
  const ds = String(row?.display_status || "").toLowerCase();
  if (ds === "grace" || row?.in_grace) {
    const left =
      row.grace_days_left != null && !Number.isNaN(Number(row.grace_days_left))
        ? Math.max(0, Number(row.grace_days_left))
        : null;
    if (left == null) return "Grace";
    if (left <= 0) return "Grace · Last day";
    return `Grace · ${left} day${left === 1 ? "" : "s"} left`;
  }
  if (ds === "expired") return "Service Expired";
  if (ds === "upcoming") return "Upcoming";
  return null;
};

const SummaryTile = ({ label, value, color, icon }) => (
  <Box
    sx={{
      p: 1.75,
      height: "100%",
      borderRadius: "14px",
      border: `1px solid ${alpha(color, 0.28)}`,
      background: `linear-gradient(145deg, ${alpha(color, 0.1)} 0%, #fff 70%)`,
      display: "flex",
      alignItems: "center",
      gap: 1.35,
    }}
  >
    <Avatar
      sx={{
        width: 40,
        height: 40,
        bgcolor: alpha(color, 0.14),
        color,
        border: `1px solid ${alpha(color, 0.22)}`,
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.06em",
          color: crmColors.text.secondary,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "1.35rem", color: crmColors.text.primary, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const Renewals = () => {
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [yearAnchor, setYearAnchor] = useState(null);

  const [yearRows, setYearRows] = useState([]);
  const [monthCounts, setMonthCounts] = useState(() => {
    const m = {};
    for (let i = 1; i <= 12; i += 1) m[i] = 0;
    return m;
  });
  const [loading, setLoading] = useState(true);
  const [billOpen, setBillOpen] = useState(false);
  const [billSeed, setBillSeed] = useState({ school: "", product: "" });
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [payMethods, setPayMethods] = useState([]);

  //Created By Cursor — catalog for MdlAddNewBill on Renew
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [productsRes, taxRes, payRes] = await Promise.all([
        apiService.getProductRateList(),
        apiService.getTaxList(),
        apiService.getPaymentMethodList(),
      ]);
      if (cancelled) return;
      if (productsRes.success) {
        setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      }
      if (taxRes.success) {
        setTaxes(Array.isArray(taxRes.data) ? taxRes.data : []);
      }
      if (payRes.success) {
        setPayMethods(Array.isArray(payRes.data) ? payRes.data : []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  //Created By Cursor — open Add New Bill dialog on Renewals (no navigate)
  const openRenewBill = (row) => {
    setBillSeed({
      school: row?.school_code || "",
      product: row?.product_code || "",
    });
    setBillOpen(true);
  };

  //Created By Cursor — years from 2019 through 2030
  const yearOptions = useMemo(() => {
    const list = [];
    for (let i = 2019; i <= 2030; i += 1) list.push(i);
    return list;
  }, []);

  //Created By Cursor — load full year once; filter months client-side
  const fetchYearRenewals = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getUpcomingRenewals({ year: selectedYear });
    if (result.success) {
      const rows = Array.isArray(result.data) ? result.data : [];
      setYearRows(rows);
      const counts = {};
      for (let i = 1; i <= 12; i += 1) counts[i] = 0;
      if (result.month_counts && typeof result.month_counts === "object") {
        Object.keys(result.month_counts).forEach((k) => {
          const n = Number(k);
          if (n >= 1 && n <= 12) counts[n] = Number(result.month_counts[k]) || 0;
        });
      } else {
        rows.forEach((r) => {
          const m = Number(r.expiry_month_num);
          if (m >= 1 && m <= 12) counts[m] += 1;
        });
      }
      setMonthCounts(counts);
    } else {
      toast.error(result.error || "Failed to load renewals");
      setYearRows([]);
      const empty = {};
      for (let i = 1; i <= 12; i += 1) empty[i] = 0;
      setMonthCounts(empty);
    }
    setLoading(false);
  }, [selectedYear]);

  useEffect(() => {
    fetchYearRenewals();
  }, [fetchYearRenewals]);

  const renewals = useMemo(() => {
    if (!selectedMonth) return yearRows;
    return yearRows.filter((r) => Number(r.expiry_month_num) === Number(selectedMonth));
  }, [yearRows, selectedMonth]);

  const yearTotal = yearRows.length;
  const monthTotal = renewals.length;

  const monthGraceCount = useMemo(
    () =>
      renewals.filter(
        (r) => r.in_grace || String(r.display_status || "").toLowerCase() === "grace"
      ).length,
    [renewals]
  );

  const monthUrgentCount = useMemo(
    () =>
      renewals.filter((r) => {
        const inGrace = r.in_grace || String(r.display_status || "").toLowerCase() === "grace";
        if (inGrace) return true;
        return Number(r.days_left) <= 7;
      }).length,
    [renewals]
  );

  const monthRevenue = useMemo(
    () => renewals.reduce((sum, r) => sum + (Number(r.renewal_rate) || 0), 0),
    [renewals]
  );

  const daysInSelectedMonth = useMemo(() => {
    if (!selectedMonth) return 30;
    return new Date(selectedYear, selectedMonth, 0).getDate();
  }, [selectedYear, selectedMonth]);

  return (
    <PageContentLayout
      title="Renewals"
      subtitle={`${MONTH_FULL[selectedMonth - 1]} ${selectedYear} · service end dates`}
      darkBar
      barSubtitle={`${monthTotal} this month · ${yearTotal} in ${selectedYear}`}
      breadcrumbs={[
        { label: "Renewals", icon: <RenewalsIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            disableElevation
            endIcon={<ArrowDownIcon />}
            startIcon={<CalendarIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => setYearAnchor(e.currentTarget)}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              height: 36,
              bgcolor: alpha("#fff", 0.16),
              color: "#fff",
              border: `1px solid ${alpha("#fff", 0.35)}`,
              "&:hover": { bgcolor: alpha("#fff", 0.24) },
            }}
          >
            {selectedYear}
          </Button>
          <Menu
            anchorEl={yearAnchor}
            open={Boolean(yearAnchor)}
            onClose={() => setYearAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 0.75,
                borderRadius: 2,
                border: `1px solid ${crmColors.border.default}`,
                boxShadow: "0 12px 28px -16px rgba(15,31,47,0.35)",
                maxHeight: 320,
              },
            }}
          >
            {yearOptions.map((y) => (
              <MenuItem
                key={y}
                selected={y === selectedYear}
                onClick={() => {
                  setSelectedYear(y);
                  setYearAnchor(null);
                  if (y === now.getFullYear()) {
                    setSelectedMonth(now.getMonth() + 1);
                  }
                }}
                sx={{
                  fontWeight: y === selectedYear ? 800 : 600,
                  fontSize: 13,
                  minWidth: 96,
                }}
              >
                {y}
              </MenuItem>
            ))}
          </Menu>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchYearRenewals}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              height: 36,
              borderRadius: "10px",
              color: "#fff",
              borderColor: alpha("#fff", 0.45),
              "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.08) },
            }}
          >
            Refresh
          </Button>
        </Stack>
      }
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
      >
        {/* KPI strip */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 1.25,
          }}
        >
          <SummaryTile
            label="This month"
            value={monthTotal}
            color={crmColors.primary.main}
            icon={<RenewalsIcon sx={{ fontSize: 20 }} />}
          />
          <SummaryTile
            label="In grace"
            value={monthGraceCount}
            color={crmColors.error.main}
            icon={<GraceIcon sx={{ fontSize: 20 }} />}
          />
          <SummaryTile
            label="Urgent ≤7d"
            value={monthUrgentCount}
            color={crmColors.warning.main}
            icon={<UpcomingIcon sx={{ fontSize: 20 }} />}
          />
          <SummaryTile
            label="Renewal value"
            value={formatMoney(monthRevenue)}
            color={crmColors.success.main}
            icon={<RateIcon sx={{ fontSize: 20 }} />}
          />
        </Box>

        {/* Month rail */}
        <Card elevation={0} sx={{ ...shellSx, overflow: "visible" }}>
          <Box
            sx={{
              px: { xs: 1.5, md: 2 },
              pt: 1.75,
              pb: 1.5,
              background: `linear-gradient(180deg, ${alpha(crmColors.primary.main, 0.04)} 0%, #fff 55%)`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={0.75}
              sx={{ mb: 1.5 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 15, color: crmColors.text.primary }}>
                  {MONTH_FULL[selectedMonth - 1]} {selectedYear}
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 12, color: crmColors.text.secondary }}>
                  Pick a month to see renewals ending then
                </Typography>
              </Box>
              <Chip
                size="small"
                icon={<CalendarIcon sx={{ fontSize: "14px !important" }} />}
                label={`${monthTotal} due · ${yearTotal} in year`}
                sx={{
                  fontWeight: 800,
                  bgcolor: alpha(crmColors.primary.main, 0.1),
                  color: crmColors.primary.main,
                  border: `1px solid ${alpha(crmColors.primary.main, 0.22)}`,
                  "& .MuiChip-icon": { color: crmColors.primary.main },
                }}
              />
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(4, 1fr)",
                  sm: "repeat(6, 1fr)",
                  md: "repeat(12, 1fr)",
                },
                gap: 0.75,
              }}
            >
              {MONTH_SHORT.map((label, idx) => {
                const monthNum = idx + 1;
                const count = monthCounts[monthNum] || 0;
                const selected = selectedMonth === monthNum;
                const isCurrent =
                  selectedYear === now.getFullYear() && monthNum === now.getMonth() + 1;
                return (
                  <Button
                    key={label}
                    onClick={() => setSelectedMonth(monthNum)}
                    disableElevation
                    component={motion.button}
                    whileTap={{ scale: 0.97 }}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      py: 1.1,
                      borderRadius: "12px",
                      textTransform: "none",
                      flexDirection: "column",
                      gap: 0.35,
                      position: "relative",
                      border: `1.5px solid ${
                        selected
                          ? crmColors.primary.main
                          : isCurrent
                            ? alpha(crmColors.primary.main, 0.4)
                            : crmColors.border.default
                      }`,
                      bgcolor: selected
                        ? `linear-gradient(160deg, ${alpha(crmColors.primary.main, 0.16)} 0%, ${alpha(
                            crmColors.hero.end,
                            0.08
                          )} 100%)`
                        : count > 0
                          ? alpha(crmColors.warning.main, 0.05)
                          : "#fff",
                      background: selected
                        ? `linear-gradient(160deg, ${alpha(crmColors.primary.main, 0.16)} 0%, ${alpha(
                            crmColors.hero.end,
                            0.08
                          )} 100%)`
                        : count > 0
                          ? alpha(crmColors.warning.main, 0.05)
                          : "#fff",
                      color: selected ? crmColors.primary.dark : crmColors.text.primary,
                      boxShadow: selected
                        ? `0 6px 14px -8px ${alpha(crmColors.primary.main, 0.55)}`
                        : "none",
                      transition: "border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
                      "&:hover": {
                        bgcolor: selected
                          ? alpha(crmColors.primary.main, 0.18)
                          : alpha(crmColors.primary.main, 0.06),
                        borderColor: selected
                          ? crmColors.primary.main
                          : alpha(crmColors.primary.main, 0.45),
                      },
                    }}
                  >
                    {isCurrent ? (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 5,
                          right: 6,
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: crmColors.energy.main,
                        }}
                      />
                    ) : null}
                    <Typography sx={{ fontWeight: 800, fontSize: 12, lineHeight: 1.1 }}>
                      {label}
                    </Typography>
                    <Box
                      sx={{
                        minWidth: 22,
                        height: 18,
                        px: 0.6,
                        borderRadius: "9px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: 10,
                        bgcolor: selected
                          ? crmColors.primary.main
                          : count > 0
                            ? alpha(crmColors.warning.main, 0.18)
                            : alpha(crmColors.text.secondary, 0.08),
                        color: selected
                          ? "#fff"
                          : count > 0
                            ? crmColors.warning.dark
                            : crmColors.text.secondary,
                      }}
                    >
                      {count}
                    </Box>
                  </Button>
                );
              })}
            </Box>
          </Box>
        </Card>

        {/* List */}
        <Card elevation={0} sx={shellSx}>
          <Box
            sx={{
              px: { xs: 1.75, md: 2.25 },
              py: 1.5,
              borderBottom: `1px solid ${crmColors.border.default}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              background: `linear-gradient(90deg, ${alpha(crmColors.primary.main, 0.05)} 0%, transparent 70%)`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: alpha(crmColors.primary.main, 0.12),
                  color: crmColors.primary.main,
                }}
              >
                <RenewalsIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>
                  Renewals list
                </Typography>
                <Typography sx={{ fontSize: 12, color: crmColors.text.secondary, fontWeight: 600 }}>
                  {MONTH_FULL[selectedMonth - 1]} {selectedYear}
                </Typography>
              </Box>
            </Stack>
            <Chip
              size="small"
              label={`${monthTotal}`}
              sx={{
                fontWeight: 800,
                bgcolor: alpha(crmColors.primary.main, 0.12),
                color: crmColors.primary.main,
              }}
            />
          </Box>

          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.25,
                  py: 8,
                }}
              >
                <CircularProgress size={34} sx={{ color: crmColors.primary.main }} />
                <Typography variant="body2" sx={{ color: crmColors.text.secondary, fontWeight: 600 }}>
                  Loading renewals…
                </Typography>
              </Box>
            ) : renewals.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    mx: "auto",
                    mb: 1.5,
                    bgcolor: alpha(crmColors.primary.main, 0.08),
                    color: crmColors.primary.main,
                  }}
                >
                  <CalendarIcon />
                </Avatar>
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No renewals this month</Typography>
                <Typography variant="body2" color="text.secondary">
                  Nothing ending in {MONTH_FULL[selectedMonth - 1]} {selectedYear}. Try another month.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: alpha(crmColors.primary.main, 0.035),
                        "& th": {
                          fontWeight: 800,
                          fontSize: 11,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: crmColors.text.secondary,
                          borderBottom: `1px solid ${crmColors.border.default}`,
                          py: 1.25,
                        },
                      }}
                    >
                      <TableCell>School</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell sx={{ minWidth: 128, width: 128, whiteSpace: "nowrap" }}>Expires</TableCell>
                      <TableCell sx={{ minWidth: 128, width: 128, whiteSpace: "nowrap" }}>Grace ends</TableCell>
                      <TableCell>Timeline</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renewals.map((row, index) => {
                      const daysLeft = Number(row.days_left);
                      const statusLabel = getRenewalStatusLabel(row);
                      const inGrace =
                        Boolean(row.in_grace) ||
                        String(row.display_status || "").toLowerCase() === "grace";
                      const isExpired =
                        String(row.display_status || "").toLowerCase() === "expired";
                      const progress =
                        daysLeft < 0
                          ? 100
                          : Math.max(
                              0,
                              Math.min(
                                100,
                                ((daysInSelectedMonth - daysLeft) / daysInSelectedMonth) * 100
                              )
                            );
                      const tone = inGrace || isExpired
                        ? crmColors.error.main
                        : daysLeft <= 7
                          ? crmColors.warning.main
                          : crmColors.success.main;

                      return (
                        <TableRow
                          key={`${row.school_code}-${row.subscription_id || row.order_id}-${row.product_code}`}
                          component={motion.tr}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.35) }}
                          hover
                          sx={{
                            "& td": {
                              borderBottom: `1px solid ${crmColors.border.default}`,
                              py: 1.35,
                              verticalAlign: "middle",
                            },
                            "&:hover": {
                              bgcolor: alpha(crmColors.primary.main, 0.03),
                            },
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1.1} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 34,
                                  height: 34,
                                  fontSize: 13,
                                  fontWeight: 800,
                                  bgcolor: alpha(crmColors.primary.main, 0.12),
                                  color: crmColors.primary.main,
                                }}
                              >
                                {(row.school_name || "S").charAt(0).toUpperCase()}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 800,
                                    color: crmColors.text.primary,
                                    lineHeight: 1.25,
                                  }}
                                >
                                  {row.school_name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: crmColors.text.secondary, fontWeight: 600 }}
                                >
                                  {row.school_code}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                              {row.product_name || row.product_code || "—"}
                            </Typography>
                            {row.product_name && row.product_code ? (
                              <Typography
                                variant="caption"
                                sx={{ color: crmColors.text.secondary, fontWeight: 600 }}
                              >
                                {row.product_code}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {statusLabel ? (
                              <Chip
                                size="small"
                                label={statusLabel}
                                sx={{
                                  fontWeight: 800,
                                  maxWidth: 210,
                                  color: "#fff",
                                  bgcolor: inGrace || isExpired
                                    ? crmColors.error.main
                                    : crmColors.success.main,
                                  borderRadius: "8px",
                                  height: 24,
                                  "& .MuiChip-label": {
                                    px: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 128, width: 128, whiteSpace: "nowrap" }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {formatDate(row.service_expiry)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 128, width: 128, whiteSpace: "nowrap" }}>
                            {row.grace_end_date ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 800,
                                  color: inGrace || isExpired
                                    ? crmColors.error.main
                                    : crmColors.text.primary,
                                }}
                              >
                                {formatDate(row.grace_end_date)}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 132 }}>
                            <Chip
                              size="small"
                              label={
                                inGrace && row.grace_days_left != null
                                  ? `${row.grace_days_left}d grace`
                                  : daysLeft < 0
                                    ? `${Math.abs(daysLeft)}d overdue`
                                    : daysLeft === 0
                                      ? "Today"
                                      : `${daysLeft} days`
                              }
                              sx={{
                                fontWeight: 800,
                                height: 22,
                                color: "#fff",
                                bgcolor: tone,
                                borderRadius: "7px",
                              }}
                            />
                            <LinearProgress
                              variant="determinate"
                              value={
                                inGrace && row.grace_days_left != null
                                  ? Math.max(
                                      0,
                                      Math.min(
                                        100,
                                        ((Number(row.grace_days || 7) - Number(row.grace_days_left)) /
                                          Math.max(1, Number(row.grace_days || 7))) *
                                          100
                                      )
                                    )
                                  : progress
                              }
                              sx={{
                                mt: 1,
                                height: 5,
                                borderRadius: 3,
                                bgcolor: alpha(tone, 0.14),
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 3,
                                  bgcolor: tone,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                              {formatMoney(row.renewal_rate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.75}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RenewalsIcon sx={{ fontSize: 14 }} />}
                                disabled={
                                  !row.school_code || !products.length || !taxes.length
                                }
                                onClick={() => openRenewBill(row)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 800,
                                  fontSize: 11,
                                  height: 30,
                                  minWidth: 96,
                                  px: 1.25,
                                  borderRadius: "8px",
                                  borderColor: crmColors.border.default,
                                  color: crmColors.text.secondary,
                                  "&:hover": {
                                    borderColor: crmColors.primary.main,
                                    color: crmColors.primary.main,
                                    bgcolor: alpha(crmColors.primary.main, 0.04),
                                  },
                                }}
                              >
                                Renew
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                disabled={!row.school_code}
                                startIcon={<DetailsIcon sx={{ fontSize: 14 }} />}
                                onClick={() =>
                                  navigate(
                                    `/schools/detail/${encodeURIComponent(row.school_code)}`
                                  )
                                }
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 800,
                                  fontSize: 11,
                                  height: 30,
                                  minWidth: 96,
                                  px: 1.25,
                                  borderRadius: "8px",
                                  bgcolor: crmColors.primary.main,
                                  "&:hover": { bgcolor: crmColors.primary.dark },
                                }}
                              >
                                Details
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <MdlAddNewBill
        open={billOpen}
        onClose={() => setBillOpen(false)}
        onCreated={() => fetchYearRenewals()}
        products={products}
        taxes={taxes}
        payMethods={payMethods}
        initialSchoolCode={billSeed.school}
        initialProductCode={billSeed.product}
      />
    </PageContentLayout>
  );
};

export default Renewals;
